import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import "intl-tel-input/build/js/utils";

export default function TestModal() {
    const [isOpen, setIsOpen] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false); // <-- Tracks loading state
    const dialogRef = useRef(null);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        phone: ''
    });

    const phoneInputRef = useRef(null);
    const itiRef = useRef(null);

    /* ================= INITIALIZE PHONE INPUT ================= */
    useEffect(() => {
        if (!phoneInputRef.current) return;

        itiRef.current = intlTelInput(phoneInputRef.current, {
            initialCountry: "auto",
            nationalMode: false,
            separateDialCode: false,
            autoPlaceholder: "polite",
            geoIpLookup: (callback) => {
                fetch("https://ipapi.co")
                    .then(res => res.json())
                    .then(data => callback(data.country_code))
                    .catch(() => callback("us"));
            }
        });

        return () => itiRef.current?.destroy();
    }, []);

    /* ================= HANDLERS ================= */
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const iti = itiRef.current;
        const phone = iti ? iti.getNumber() : "";

        if (!phone || phone.length < 8) {
            alert("Please enter a valid phone number");
            return;
        }

        const payload = { ...formData, phone };

        try {
            setIsSubmitting(true); // <-- Start visual loading feedback

            const response = await fetch(process.env.REACT_APP_API_URL_EMAIL + '/send-enquiry', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (response.ok) {
                alert(result.message || 'Message sent successfully!');
                setFormData({
                    name: '',
                    email: '',
                    subject: '',
                    message: '',
                });
                if (iti) {
                    iti.setNumber("");
                }
            } else {
                alert('Failed to send message. Please try again.');
            }
        } catch (error) {
            console.error(error);
            alert('An error occurred. Please try again later.');
        } finally {
            setIsSubmitting(false); // <-- Reset loading feedback
        }
    };

    /* ================= DIALOG CORE LIFECYCLE ================= */
    useEffect(() => {
        if (isOpen) {
            dialogRef.current?.showModal();
        }
    }, [isOpen]);

    const openModal = (e) => {
        e.preventDefault();
        setIsOpen(true);
        dialogRef.current?.showModal();
    };

    const closeModal = () => {
        setIsOpen(false);
        dialogRef.current?.close();
        navigate(-1);
    };

    return (
        <>
            <div>
                <div className="container-fluid navyblue-bg py-5 mb-5 hero-header">
                    <div className="container py-5">
                        <div className="row justify-content-center py-5">
                            <div className="col-lg-10 pt-lg-5 mt-lg-5 text-center">
                                <h1 className="display-3 text-white animated slideInDown">
                                    Enquiry
                                </h1>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container">

                {/* Centered & Adaptive Modal Dialog */}
                <dialog ref={dialogRef} onClose={closeModal} className="modal-box">
                    <button onClick={closeModal} className="close-btn" aria-label="Close modal">
                        &times;
                    </button>

                    <div className="modal-content-wrapper">
                        <h1 className="modal-title mb-4">Enquiry Form</h1>
                        <form onSubmit={handleSubmit} method="POST">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            name='name'
                                            className="form-control"
                                            id="name"
                                            placeholder="Your Name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label htmlFor="name">Your Name</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating">
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            name="email"
                                            placeholder="Your Email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label htmlFor="email">Your Email</label>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="form-group intl-input-wrapper">
                                        <input
                                            type="tel"
                                            id='phone'
                                            name='phone'
                                            ref={phoneInputRef}
                                            className="form-control"
                                            placeholder="Your Phone"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="subject"
                                            name="subject"
                                            placeholder="Subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label htmlFor="subject">Subject</label>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="form-floating">
                                        <textarea
                                            className="form-control"
                                            placeholder="Leave a message here"
                                            id="message"
                                            style={{ height: 100 }}
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label htmlFor="message">Message</label>
                                    </div>
                                </div>

                                {/* Responsive Submit Button Layout */}
                                <div className="col-12">
                                    <button
                                        className={`navyblue-bg w-100 py-3 submit-btn ${isSubmitting ? 'loading' : ''}`}
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <span className="btn-content">
                                                <span className="spinner"></span>
                                                Sending Message...
                                            </span>
                                        ) : (
                                            "Send Message"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </dialog>

                {/* Component Embedded Stylesheet */}
                <style>{`
                .container {
                    font-family: sans-serif;
                    padding: 40px;
                }

                .modal-link {
                    color: #0066cc;
                    text-decoration: underline;
                }

                /* ================= RESPONSIVE DIALOG BOX ================= */
                .modal-box {
                    border: none;
                    border-radius: 12px;
                    padding: 2.5rem 2rem 2rem 2rem;

                    background: #1a252f;box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);position: fixed;top: 50%;left: 50%;transform: translate(-50%, -50%);
                    margin: 0;
                    width: 90%;
                    max-width: 650px;
                    max-height: 85vh;
                    overflow-y: auto;
                    }
                    .modal-box::backdrop {
                    background-color: rgba(0, 0, 0, 0.6);backdrop-filter: blur(4px);
                    }
                    .close-btn {
                    position: absolute;top: 15px;
                    right: 20px;background: transparent;border: none;
                    color: #ffffff;
                    font-size: 1.75rem;
                    cursor: pointer;
                    line-height: 1;
                    opacity: 0.7;
                    transition: opacity 0.2s ease;z-index: 10;
                    }
                    .close-btn:hover {
                    opacity: 1;
                    }
                    .modal-title {
                    color: #ffffff;
                    font-weight: 600;
                    }
                    /* ================= PHONE INPUT INTEGRATION STYLES ================= */
                    .intl-input-wrapper .iti {
                    display: block !important;
                    width: 100% !important;
                    }
                    .intl-input-wrapper input[type="tel"] {width: 100% !important;height: calc(3.5rem + 2px) !important;border-radius: 0.375rem !important;padding-left: 52px !important;}/* ================= BUTTON RESPONSIVENESS AND INTERACTION ================= */.navyblue-bg {background-color: #0d6efd;color: white;border: none;border-radius: 6px;font-weight: bold;cursor: pointer;display: flex;justify-content: center;align-items: center;transition: all 0.2s ease-in-out;}/* Hover effect */.navyblue-bg:hover:not(:disabled) {background-color: #0b5ed7;transform: translateY(-1px);box-shadow: 0 4px 12px rgba(13, 110, 245, 0.3);}/* Active press click contraction */.navyblue-bg:active:not(:disabled) {transform: translateY(1px);box-shadow: none;}/* Style state when submitting */.navyblue-bg:disabled {background-color: #5c9dfa !important;cursor: not-allowed;opacity: 0.8;transform: none !important;box-shadow: none !important;}.btn-content {display: inline-flex;align-items: center;gap: 10px;}/* Pure CSS Spinner Graphic */.spinner {width: 18px;height: 18px;border: 2px solid rgba(255, 255, 255, 0.3);border-radius: 50%;border-top-color: #ffffff;animation: spin 0.8s linear infinite;}@keyframes spin {to { transform: rotate(360deg); }}/* Mobile Sizing Adapters */@media (max-width: 576px) {.modal-box {padding: 2rem 1.25rem 1.5rem 1.25rem;width: 95%;max-height: 90vh;}.close-btn {top: 10px;right: 15px;}}`}</style>
            </div>
        </>
    )
}